import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://sghmpmjszcuvfdinsbxq.supabase.co';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNnaG1wbWpzemN1dmZkaW5zYnhxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI3ODgzNDgsImV4cCI6MjA4ODM2NDM0OH0.uzNTmC5HCBer2BFrz8VPYPAJBxwlNxA6mjzZr6Hs1cA';

// One-time migration: move session from old custom storageKey to Supabase default
try {
  const OLD_KEY = 'ojthub-auth';
  const DEFAULT_KEY = `sb-${new URL(SUPABASE_URL).hostname.split('.')[0]}-auth-token`;
  const old = localStorage.getItem(OLD_KEY);
  if (old && !localStorage.getItem(DEFAULT_KEY)) {
    localStorage.setItem(DEFAULT_KEY, old);
    localStorage.removeItem(OLD_KEY);
  }
} catch(e) {}

// noOpLock bypasses the broken Web Locks API that causes infinite hangs
// See: https://github.com/supabase/supabase-js/issues/1594
const noOpLock = async (name, acquireTimeout, fn) => { return await fn(); };

// Singleton — prevents multiple GoTrueClient instances competing for the lock
const _globalKey = '__supabase_client__';
if (!window[_globalKey]) {
  window[_globalKey] = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      lock: noOpLock,
    }
  });
}

export const supabase = window[_globalKey];

export const authHelpers = {
  signUpWithEmail: async (email, password, meta = {}) => {
    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: meta } });
    return { data, error };
  },
  signInWithEmail: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  },
  signInWithGoogle: async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin },
    });
    return { data, error };
  },
  signOut: async () => {
    const { error } = await supabase.auth.signOut();
    return { error };
  },
  getUser: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },
  onAuthChange: (callback) => {
    return supabase.auth.onAuthStateChange((_event, session) => {
      callback(session?.user || null);
    });
  },
};

export const userHelpers = {
  getProfile: async (userId) => {
    const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
    return { data, error };
  },
  upsertProfile: async (profile) => {
    const { data, error } = await supabase.from('profiles').upsert(profile, { onConflict: 'id' }).select().single();
    return { data, error };
  },
};

export const jobHelpers = {
  getJobs: async ({ country = 'PH', page = 1, search = '', limit = 20 } = {}) => {
    let query = supabase.from('job_listings').select('*').eq('is_active', true)
      .order('is_featured', { ascending: false }).order('created_at', { ascending: false })
      .range((page - 1) * limit, page * limit - 1);
    if (country !== 'ALL') query = query.eq('country', country);
    if (search) query = query.ilike('title', `%${search}%`);
    const { data, error } = await query;
    return { data: data || [], error };
  },
  getJobById: async (id) => {
    const { data, error } = await supabase.from('job_listings').select('*').eq('id', id).single();
    return { data, error };
  },
};

export const applicationHelpers = {
  getMyApplications: async (userId) => {
    const { data, error } = await supabase.from('applications').select('*')
      .eq('student_id', userId).order('applied_at', { ascending: false });
    return { data: data || [], error };
  },
  apply: async ({ studentId, jobId, coverNote = '' }) => {
    const { data, error } = await supabase.from('applications')
      .insert({ student_id: studentId, job_id: jobId, cover_note: coverNote, status: 'pending' })
      .select().single();
    return { data, error };
  },
  updateStatus: async (applicationId, status, notes = '') => {
    const { error } = await supabase.from('applications')
      .update({ status, employer_notes: notes }).eq('id', applicationId);
    return { data: null, error };
  },
  checkIfApplied: async (studentId, jobId) => {
    const { data } = await supabase.from('applications').select('id, status')
      .eq('student_id', studentId).eq('job_id', jobId).maybeSingle();
    return data;
  },
};

export const logbookHelpers = {
  getEntries: async (userId) => {
    const { data, error } = await supabase.from('logbook_entries').select('*')
      .eq('student_id', userId).order('entry_date', { ascending: false });
    return { data: data || [], error };
  },
  upsertEntry: async (entry) => {
    const { data, error } = await supabase.from('logbook_entries')
      .upsert(entry, { onConflict: 'id' }).select().single();
    return { data, error };
  },
  deleteEntry: async (id) => {
    const { error } = await supabase.from('logbook_entries').delete().eq('id', id);
    return { error };
  },
  getTotalHours: async (userId) => {
    const { data, error } = await supabase.from('logbook_entries').select('hours').eq('student_id', userId);
    if (error) return 0;
    return (data || []).reduce((sum, e) => sum + (parseFloat(e.hours) || 0), 0);
  },
};

export const documentHelpers = {
  getMyDocuments: async (userId) => {
    const { data, error } = await supabase.from('student_documents').select('*').eq('student_id', userId);
    return { data: data || [], error };
  },
  uploadFile: async (userId, docType, file) => {
    const ext = file.name.split('.').pop();
    const path = `student-docs/${userId}/${docType}.${ext}`;
    const { error } = await supabase.storage.from('documents').upload(path, file, { upsert: true });
    if (error) return { url: null, error };
    const { data: urlData } = supabase.storage.from('documents').getPublicUrl(path);
    return { url: urlData.publicUrl, error: null };
  },
};

export const universityHelpers = {
  getUniversities: async () => {
    const { data, error } = await supabase.from('universities').select('*').order('name');
    return { data: data || [], error };
  },
};

export const employerHelpers = {
  getMyCompany: async (userId) => {
    const { data, error } = await supabase.from('companies').select('*').eq('owner_id', userId).maybeSingle();
    return { data, error };
  },
};

export async function sendEmail(template, data, to = null) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch(
      "https://sghmpmjszcuvfdinsbxq.supabase.co/functions/v1/send-email",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({ template, data, to }),
      }
    );
    return await res.json();
  } catch (e) {
    console.warn("sendEmail failed:", e.message);
    return { ok: false };
  }
}
 
